/**
 * Donation Form
 */
import classnames from 'classnames';
import compact from 'lodash/compact';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import map from 'lodash/fp/map';
import MaskedInput from 'react-text-mask';
import React, { Fragment, createRef } from 'react';
import toNumber from 'lodash/toNumber';
import { Formik, Field, Form } from 'formik';
import { PropTypes } from 'prop-types';
// import { useSpring, animated } from '@react-spring/web';

/* eslint-disable jsx-a11y/label-has-for */
/* global window */
const DonationForm = ({
  buttonText,
  classyFormUrl,
  donationType,
  entityId,
  monthlyValues,
  oneTimeValues,
}) => {
  const ref = createRef();
  const numberMask = createNumberMask({
    prefix: '$ ',
    suffix: '',
  });
  const getClassyUrl = (unformatted, amount, recurring) => {
    const url = classyFormUrl.replace('#!/donation/checkout', '');
    const parsed = new URL(url);
    const connector = parsed.search ? '&' : '?';
    return `${url}${connector}amount=${amount}&recurring=${recurring}`.replace('?', '#!/donation/checkout?');
  };
  let defaultDonationType = donationType;
  if (defaultDonationType === 'monthly') {
    defaultDonationType = monthlyValues.length ? 'monthly' : 'onetime';
  } else {
    defaultDonationType = oneTimeValues.length ? 'onetime' : 'monthly';
  }
  let initialMonthly;
  let initialOneTime;
  if (monthlyValues.length > 1) {
    initialMonthly = monthlyValues[monthlyValues.length - 2];
  } else if (monthlyValues.length > 0) {
    [initialMonthly] = monthlyValues;
  }
  if (oneTimeValues.length > 1) {
    initialOneTime = oneTimeValues[oneTimeValues.length - 2];
  } else if (oneTimeValues.length > 0) {
    [initialOneTime] = oneTimeValues;
  }
  return (
    <div>
      <Formik
        initialValues={{
          [`donation-type-${entityId}`]: defaultDonationType,
          [`other-monthly-${entityId}`]: '',
          [`other-one-time-${entityId}`]: '',
          [`monthly-donation-amount-${entityId}`]: initialMonthly,
          [`one-time-donation-amount-${entityId}`]: initialOneTime,
        }}
        onSubmit={(values) => {
          switch (values[`donation-type-${entityId}`]) {
            case 'monthly': {
              let { [`monthly-donation-amount-${entityId}`]: amount } = values;
              const { [`other-monthly-${entityId}`]: other } = values;
              if (amount === 'other') amount = other.replace(/\D/g, '');
              amount = toNumber(amount);
              if (amount > 0) {
                window.location.href = getClassyUrl(classyFormUrl, amount, 1);
              }
              break;
            }
            case 'onetime': {
              let { [`one-time-donation-amount-${entityId}`]: amount } = values;
              const { [`other-one-time-${entityId}`]: other } = values;
              if (amount === 'other') amount = other.replace(/\D/g, '');
              amount = toNumber(amount);
              if (amount > 0) {
                window.location.href = getClassyUrl(classyFormUrl, amount, 0);
              }
              break;
            }
            default:
              break;
          }
        }}
      >
        {({ values, setStatus, status }) => {
          const type = values[`donation-type-${entityId}`].replace('onetime', 'one-time');
          const amount = values[`${type}-donation-amount-${entityId}`];
          const combined = `${type}-${amount}`;
          // If the donation amount and/or type has changed and "other" is not
          // the selected amount (since you couldn't immediately submit without
          // entering a value) then we'll animate the button.
          if ((status !== combined) && amount !== 'other') {
            setStatus(combined);
            const { current } = ref;
            if (current) {
              current.classList.remove('is-animating');
              current.classList.add('is-animating');
              setTimeout(() => current.classList.remove('is-animating'), 500);
            }
          }
          return (
            <Form>
              {/* Select donation type */}
              <div className="sr-only" id={`donation-type-label-${entityId}`}>Select type of donation</div>
              {(oneTimeValues.length && monthlyValues.length) ? (
                <div role="group" aria-labelledby={`donation-type-label-${entityId}`}>
                  <label
                    className={classnames({
                      'is-active': values[`donation-type-${entityId}`] === 'monthly',
                      'donation-type-label': true,
                    })}
                    htmlFor={`donation-type-monthly-${entityId}`}
                  >
                    <Field
                      id={`donation-type-monthly-${entityId}`}
                      name={`donation-type-${entityId}`}
                      type="radio"
                      value="monthly"
                    />
                    Monthly
                  </label>
                  <label
                    className={classnames({
                      'is-active': values[`donation-type-${entityId}`] === 'onetime',
                      'donation-type-label': true,
                    })}
                    htmlFor={`donation-type-one-time-${entityId}`}
                  >
                    <Field
                      id={`donation-type-one-time-${entityId}`}
                      name={`donation-type-${entityId}`}
                      type="radio"
                      value="onetime"
                    />
                    One-Time
                  </label>
                </div>
              ) : null}
              {/* Donation donation amount */}
              <div>
                {values[`donation-type-${entityId}`] === 'monthly' ? (
                  <Fragment>
                    {/* Generate monthly donation options */}
                    <div className="sr-only" id={`donation-monthly-label-${entityId}`}>Make a monthly donation</div>
                    <div role="group" aria-labelledby={`donation-monthly-label-${entityId}`}>
                      {map(amount => (
                        <label
                          className={classnames({
                            'is-active': amount === values[`monthly-donation-amount-${entityId}`],
                            'monthly-donation-amount-label': true,
                          })}
                          key={`monthly-donation-amount-${amount}-${entityId}`}
                          htmlFor={`monthly-donation-amount-${amount}-${entityId}`}
                        >
                          <Field
                            id={`monthly-donation-amount-${amount}-${entityId}`}
                            name={`monthly-donation-amount-${entityId}`}
                            type="radio"
                            value={amount}
                          />
                          {new Intl.NumberFormat(
                            'en-US',
                            { style: 'currency', currency: 'USD', minimumFractionDigits: 0 },
                          ).format(amount)}
                        </label>
                      ), compact(monthlyValues))}
                      <label
                        key={`monthly-donation-amount-other-${entityId}`}
                        htmlFor={`monthly-donation-amount-other-${entityId}`}
                        className={classnames({
                          'is-active': values[`monthly-donation-amount-${entityId}`] === 'other',
                          'monthly-donation-amount-label': true,
                        })}
                      >
                        <Field
                          id={`monthly-donation-amount-other-${entityId}`}
                          type="radio"
                          name={`monthly-donation-amount-${entityId}`}
                          value="other"
                        />
                        {values[`monthly-donation-amount-${entityId}`] === 'other' ? (
                          <Field
                            name={`other-monthly-${entityId}`}
                            render={({ field }) => (
                              <MaskedInput
                                autoFocus
                                mask={numberMask}
                                placeholder="$0"
                                {...field}
                              />
                            )}
                          />
                        ) : (
                          <span>Other</span>
                        )}
                      </label>
                    </div>
                  </Fragment>
                ) : (
                  <Fragment>
                    {/* Generate one-time donation options */}
                    <div className="sr-only" id={`donation-one-time-label-${entityId}`}>Make a one-time donation</div>
                    <div role="group" aria-labelledby={`donation-one-time-label-${entityId}`}>
                      {map(amount => (
                        <label
                          className={classnames({
                            'is-active': amount === values[`one-time-donation-amount-${entityId}`],
                            'one-time-donation-amount-label': true,
                          })}
                          key={`one-time-donation-amount-${amount}-${entityId}`}
                          htmlFor={`one-time-donation-amount-${amount}-${entityId}`}
                        >
                          <Field
                            id={`one-time-donation-amount-${amount}-${entityId}`}
                            name={`one-time-donation-amount-${entityId}`}
                            type="radio"
                            value={amount}
                          />
                          {new Intl.NumberFormat(
                            'en-US',
                            { style: 'currency', currency: 'USD', minimumFractionDigits: 0 },
                          ).format(amount)}
                        </label>
                      ), compact(oneTimeValues))}
                      <label
                        className={classnames({
                          'is-active': values[`one-time-donation-amount-${entityId}`] === 'other',
                          'one-time-donation-amount-label': true,
                        })}
                        key={`one-time-donation-amount-other-${entityId}`}
                        htmlFor={`one-time-donation-amount-other-${entityId}`}
                      >
                        <Field
                          id={`one-time-donation-amount-other-${entityId}`}
                          type="radio"
                          name={`one-time-donation-amount-${entityId}`}
                          value="other"
                        />
                        {values[`one-time-donation-amount-${entityId}`] === 'other' ? (
                          <Field
                            name={`other-one-time-${entityId}`}
                            render={({ field }) => (
                              <MaskedInput
                                autoFocus
                                mask={numberMask}
                                placeholder="$0"
                                {...field}
                              />
                            )}
                          />
                        ) : (
                          <span>Other</span>
                        )}
                      </label>
                    </div>
                  </Fragment>
                )}
              </div>
              <button ref={ref} type="submit">
                {buttonText}
              </button>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

DonationForm.propTypes = {
  buttonText: PropTypes.string.isRequired,
  classyFormUrl: PropTypes.string.isRequired,
  donationType: PropTypes.string.isRequired,
  entityId: PropTypes.string.isRequired,
  monthlyValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  oneTimeValues: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default DonationForm;
